import {
  Component,
  Host,
  ViewChild,
  ElementRef,
  Inject
} from '@angular/core';

import { NgClass, AsyncPipe } from '@angular/common';
import { PipesModule } from '../../../pipes/pipes.module';
import { IconComponent } from '../../icon/icon.component';
import { FocusOnDisabledDirective } from '../../focus/focus-on-disabled.directive';
import { DatePickerButtonComponent } from '../date-picker-button/date-picker-button.component';
import { DatePickerInnerComponent } from '../date-picker-inner/date-picker-inner.component';

import type { DateObject } from '../date-picker-inner/date-picker-inner.domain';
import type { OnInit } from '@angular/core';

@Component({
  selector: 'td-wb-month-picker',
  templateUrl: './month-picker.component.html',
  styleUrls: ['./month-picker.component.scss'],
  imports: [DatePickerButtonComponent, FocusOnDisabledDirective, IconComponent, NgClass, AsyncPipe, PipesModule]
})
export class MonthPickerComponent implements OnInit {

  /**
   * Number of months in year
   */
  private static readonly NUMBER_MONTHS_IN_YEAR: number = 12;

  @ViewChild('yearButton',{ static:false, read: ElementRef }) yearButtonRef: ElementRef;

  /**
   * Title of month view
   */
  title: string;

  /**
   * Array of months to display
   */
  rows: Array<Array<DateObject>> = [];

  /**
   * Date Picker Inner component
   */
  datePicker: DatePickerInnerComponent;

  /**
   * Maximum date picker mode
   */
  maxMode: string;

  /**
   * Boolean for determining whether previous button should be disabled
   */
  navPrevDisabled: boolean;

  /**
   * Boolean for determining whether next button should be disabled
   */
  navNextDisabled: boolean;

  /**
   * The constructor of the component.
   *
   * @param datePicker Date picker inner component
   */
  constructor(@Inject(DatePickerInnerComponent) @Host() datePicker: DatePickerInnerComponent) {
    this.datePicker = datePicker;
  }

  /**
   * This function registers handlers and initializes step
   */
  ngOnInit(): void {
    this.datePicker.step.set('month', { years: 1 });
    this.datePicker.setRefreshViewHandler((): void => this.refreshView(), 'month');
    this.datePicker.setCompareHandler((date1: Date, date2: Date): number => this.compareDates(date1, date2), 'month');
    this.datePicker.refreshView();
  }

  /**
   * This function is the refresh view handler for date picker inner component
   */
  refreshView(): void {
    const datePicker = this.datePicker;
    const months: Array<DateObject> = new Array(MonthPickerComponent.NUMBER_MONTHS_IN_YEAR);
    const year: number = datePicker.activeDate.getFullYear();
    let date: Date;

    for (let i = 0; i < MonthPickerComponent.NUMBER_MONTHS_IN_YEAR; i++) {
      date = new Date(year, i, 1);
      months[i] = datePicker.createDateObject(date, datePicker.formatMonth);
      months[i].uid = datePicker.uniqueId + '-' + i;
    }

    // disable navigation according to the range (maxDate and minDate)
    // if the first day of the year is not greater then minDate there is no point to navigate to a previous year
    this.navPrevDisabled = (this.datePicker.minDate !== undefined) && (months[0].date <= this.datePicker.minDate);
    // if the first day of the next year is greater then maxDate there is no point to navigate to the next year
    this.navNextDisabled = (this.datePicker.maxDate !== undefined) && (new Date(year + 1, 0, 1) > this.datePicker.maxDate);

    this.title = datePicker.formatDate(datePicker.activeDate, datePicker.formatMonthTitle);
    this.rows = datePicker.split(months, 3) as Array<Array<DateObject>>;
  }

  /**
   * This function is the compare handler for date picker inner component
   *
   * @param date1 Date to compare
   * @param date2 Date to compare
   * @returns Returns comparison of two dates
   */
  compareDates(date1: Date, date2: Date): number {
    const d1 = new Date(date1.getFullYear(), date1.getMonth());
    const d2 = new Date(date2.getFullYear(), date2.getMonth());
    return d1.getTime() - d2.getTime();
  }

}
